import { CreateProgram, OrgFullDetail, ProgramType } from "@humanex/interfaces";
import { Box, Button, ImageInput, Input, Select, TiptapEditor } from "@humanex/ui/components";
import { toast, useI18n } from "@humanex/ui/libs";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useCreateProgram, useUploadImage } from "@/hooks/queries";

interface CreateProgramPageProps {
  org: OrgFullDetail;
}

interface FormData {
  title: string;
  description: string;
  banner: string;
  type: ProgramType;
  country: string;
  location: string;
  quota: number;
  requirements: string;
  benefits: string;
  costVisible: boolean;
  depositAmount: number;
  deadline: string;
}

const PROGRAM_TYPE_OPTIONS = [
  { value: ProgramType.DU_HOC, label: "Du học" },
  { value: ProgramType.VIEC_LAM, label: "Việc làm trong nước" },
  { value: ProgramType.XUAT_KHAU, label: "Xuất khẩu lao động" },
  { value: ProgramType.HOC_NGHE, label: "Học nghề" }
];

export function CreateProgramPage({ org }: CreateProgramPageProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: createProgram } = useCreateProgram();
  const { mutateAsync: uploadImage } = useUploadImage();

  const form = useForm<FormData>({
    defaultValues: {
      title: "",
      description: "",
      banner: "",
      type: ProgramType.DU_HOC,
      country: "",
      location: "",
      quota: 1,
      requirements: "",
      benefits: "",
      costVisible: false,
      depositAmount: 0,
      deadline: ""
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        const programData: CreateProgram = {
          orgId: org.id,
          title: value.title.trim(),
          description: value.description.trim(),
          banner: value.banner.trim() || null,
          type: value.type,
          country: value.country.trim() || null,
          location: value.location.trim() || null,
          quota: value.quota,
          requirements: value.requirements.trim() || null,
          benefits: value.benefits.trim() || null,
          costVisible: value.costVisible,
          depositAmount: value.depositAmount,
          deadline: value.deadline ? new Date(value.deadline) : null
        };

        await createProgram(programData);
        toast.success("Chương trình đã được tạo thành công!");
        navigate({
          to: "/org/$orgId",
          params: { orgId: org.id }
        });
      } catch (error) {
        console.error("Error creating program:", error);
        toast.error("Có lỗi xảy ra khi tạo chương trình");
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  return (
    <div className="py-4 md:py-6 lg:py-8 px-0 sm:px-2 md:px-4 lg:px-8 space-y-6">
      <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto">
        <div className="px-4 md:px-6">
          <p className="text-responsive-xl font-normal text-text-primary mb-2">Tạo chương trình mới</p>
          <p className="text-responsive-base text-text-secondary">
            Điền thông tin để tạo chương trình mới cho tổ chức {org.name}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          {/* Basic Info */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">{t("org.edit.basicInfo")}</p>
          </div>
          <Box className="space-y-6 mb-6">
            {/* Title */}
            <form.Field
              name="title"
              validators={{
                onChange: ({ value }) => (!value?.trim() ? "Tiêu đề là bắt buộc" : undefined)
              }}
            >
              {(field) => (
                <Input
                  label="Tiêu đề chương trình"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Nhập tiêu đề chương trình"
                  required
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Program Type */}
            <form.Field
              name="type"
              validators={{
                onChange: ({ value }) => (!value ? "Loại chương trình là bắt buộc" : undefined)
              }}
            >
              {(field) => (
                <Select
                  label="Loại chương trình"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value as ProgramType)}
                  options={PROGRAM_TYPE_OPTIONS}
                  required
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Description */}
            <form.Field
              name="description"
              validators={{
                onChange: ({ value }) => (!value?.trim() ? "Mô tả là bắt buộc" : undefined)
              }}
            >
              {(field) => (
                <TiptapEditor
                  label="Mô tả chương trình"
                  placeholder="Nhập mô tả chi tiết về chương trình"
                  required
                  error={field.state.meta.errors[0] || undefined}
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Banner */}
            <form.Field name="banner">
              {(field) => (
                <ImageInput
                  value={field.state.value}
                  onChange={field.handleChange}
                  uploadFn={uploadImage}
                  label="Banner"
                  previewSize="w-full max-w-md h-48"
                  disabled={isSubmitting}
                  toastSuccess={toast.success}
                  toastError={toast.error}
                  successMessage={t("org.edit.avatarUploadSuccess")}
                  errorMessage={t("org.edit.avatarUploadError")}
                  invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                  fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                  alt="Banner preview"
                />
              )}
            </form.Field>
          </Box>

          {/* Location & Details */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">ĐỊA ĐIỂM & CHI TIẾT</p>
          </div>
          <Box className="space-y-6 mb-6">
            {/* Country */}
            <form.Field name="country">
              {(field) => (
                <Input
                  label="Quốc gia (tuỳ chọn)"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Ví dụ: Nhật Bản, Hàn Quốc"
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Location */}
            <form.Field name="location">
              {(field) => (
                <Input
                  label="Địa điểm cụ thể (tuỳ chọn)"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Ví dụ: Tokyo, Osaka"
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Quota */}
            <form.Field
              name="quota"
              validators={{
                onChange: ({ value }) => {
                  if (!value || value < 1) return "Chỉ tiêu phải ít nhất là 1";
                  return undefined;
                }
              }}
            >
              {(field) => (
                <Input
                  label="Chỉ tiêu tuyển dụng"
                  type="number"
                  value={field.state.value.toString()}
                  onChange={(e) => field.handleChange(parseInt(e.target.value) || 1)}
                  placeholder="Nhập số lượng chỉ tiêu"
                  required
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Deadline */}
            <form.Field name="deadline">
              {(field) => (
                <Input
                  label="Hạn nộp hồ sơ (tuỳ chọn)"
                  type="datetime-local"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>
          </Box>

          {/* Requirements & Benefits */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">YÊU CẦU & QUYỀN LỢI</p>
          </div>
          <Box className="space-y-6 mb-6">
            {/* Requirements */}
            <form.Field name="requirements">
              {(field) => (
                <TiptapEditor
                  label="Yêu cầu ứng viên (tuỳ chọn)"
                  placeholder="Mô tả các yêu cầu đối với ứng viên"
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Benefits */}
            <form.Field name="benefits">
              {(field) => (
                <TiptapEditor
                  label="Quyền lợi ứng viên (tuỳ chọn)"
                  placeholder="Mô tả các quyền lợi ứng viên được hưởng"
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>
          </Box>

          {/* Deposit / Cost */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">PHÍ & THANH TOÁN</p>
          </div>
          <Box className="space-y-6 mb-6">
            {/* Deposit Amount */}
            <form.Field
              name="depositAmount"
              validators={{
                onChange: ({ value }) => {
                  if (value < 0) return "Phí đặt cọc không được âm";
                  return undefined;
                }
              }}
            >
              {(field) => (
                <Input
                  label="Phí đặt cọc (VND)"
                  type="number"
                  value={field.state.value.toString()}
                  onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Cost Visible toggle */}
            <form.Field name="costVisible">
              {(field) => (
                <div className="flex items-center gap-3">
                  <input
                    id="costVisible"
                    type="checkbox"
                    checked={field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    disabled={isSubmitting}
                    className="w-4 h-4 rounded border border-border accent-text-primary"
                  />
                  <label htmlFor="costVisible" className="text-sm text-text-primary">
                    Hiển thị chi phí công khai
                  </label>
                </div>
              )}
            </form.Field>
          </Box>

          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: "/org/$orgId", params: { orgId: org.id } })}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button type="submit" variant="default" disabled={isSubmitting}>
              {isSubmitting ? "Đang tạo..." : "Tạo chương trình"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
